import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface EditSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: any;
  onSuccess: () => void;
}

export function EditSubscriptionDialog({ open, onOpenChange, subscription, onSuccess }: EditSubscriptionDialogProps) {
  const [plan, setPlan] = useState<"basic" | "standard">("basic");
  const [status, setStatus] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  
  // PTA Details
  const [ptaName, setPtaName] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [charityNumber, setCharityNumber] = useState("");
  const [sharedEmail, setSharedEmail] = useState("");

  // Addons
  const [allAddons, setAllAddons] = useState<any[]>([]);
  const [activeAddonIds, setActiveAddonIds] = useState<string[]>([]);
  const [originalAddonIds, setOriginalAddonIds] = useState<string[]>([]);

  useEffect(() => {
    if (subscription) {
      setPlan(subscription.plan || "basic");
      setStatus(subscription.status || "active");
      setExpiresAt(subscription.expires_at ? subscription.expires_at.slice(0, 10) : "");
      
      if (subscription.ptas) {
        setPtaName(subscription.ptas.name || "");
        setSchoolName(subscription.ptas.school_name || "");
        setCharityNumber(subscription.ptas.charity_number || "");
        setSharedEmail(subscription.ptas.shared_email || "");
      }
    }
  }, [subscription]);

  useEffect(() => {
    if (open && subscription?.id) {
      fetchAddons();
    }
  }, [open, subscription?.id]);

  const fetchAddons = async () => {
    // Fetch all available addons
    const { data: addons } = await supabase
      .from('pricing_addons')
      .select('id, name, price, description')
      .order('name');

    setAllAddons(addons || []);

    // Fetch active addons for this subscription
    const { data: activeRecords } = await supabase
      .from('subscription_addons')
      .select('addon_id')
      .eq('subscription_id', subscription.id);

    const ids = (activeRecords || []).map((r: any) => r.addon_id);
    setActiveAddonIds(ids);
    setOriginalAddonIds(ids);
  };

  const toggleAddon = (addonId: string) => {
    setActiveAddonIds(prev =>
      prev.includes(addonId)
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Look up the correct pricing_plan_id for the selected plan name
    const { data: matchingPlan } = await supabase
      .from('pricing_plans')
      .select('id, name')
      .ilike('name', plan)
      .limit(1)
      .maybeSingle();

    // Update subscription with plan, plan_selected, and pricing_plan_id in sync
    const updatePayload: Record<string, any> = {
      plan: plan,
      status: status,
      plan_selected: plan,
      expires_at: expiresAt ? new Date(expiresAt + 'T23:59:59Z').toISOString() : null,
    };
    if (matchingPlan?.id) {
      updatePayload.pricing_plan_id = matchingPlan.id;
    }

    const { error: subError } = await supabase
      .from('subscriptions')
      .update(updatePayload)
      .eq('id', subscription.id);

    if (subError) {
      setLoading(false);
      toast({ title: "Error", description: subError.message, variant: "destructive" });
      return;
    }

    // Update PTA details if pta_id exists
    if (subscription.pta_id) {
      const { error: ptaError } = await supabase
        .from('ptas')
        .update({
          name: ptaName,
          school_name: schoolName,
          charity_number: charityNumber,
          shared_email: sharedEmail
        })
        .eq('id', subscription.pta_id);

      if (ptaError) {
        setLoading(false);
        toast({ title: "Error updating PTA", description: ptaError.message, variant: "destructive" });
        return;
      }
    }

    // Update addons if on basic plan
    if (plan === 'basic' && subscription.pta_id) {
      const toAdd = activeAddonIds.filter(id => !originalAddonIds.includes(id));
      const toRemove = originalAddonIds.filter(id => !activeAddonIds.includes(id));

      for (const addonId of toAdd) {
        const { error } = await supabase
          .from('subscription_addons')
          .insert({
            addon_id: addonId,
            subscription_id: subscription.id,
            pta_id: subscription.pta_id
          });
        if (error) console.error('Error adding addon:', error);
      }

      for (const addonId of toRemove) {
        const { error } = await supabase
          .from('subscription_addons')
          .delete()
          .eq('subscription_id', subscription.id)
          .eq('addon_id', addonId);
        if (error) console.error('Error removing addon:', error);
      }
    }

    setLoading(false);
    toast({ title: "Success", description: "Subscription and PTA details updated successfully" });
    onSuccess();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Subscription & PTA</DialogTitle>
          <DialogDescription>Update subscription and PTA details</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Tabs defaultValue="subscription" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
              <TabsTrigger value="pta">PTA Details</TabsTrigger>
              <TabsTrigger value="addons">Add-ons</TabsTrigger>
            </TabsList>
            
            <TabsContent value="subscription" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Plan</Label>
                <Select value={plan} onValueChange={(value) => setPlan(value as "basic" | "standard")}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
              </Select>
              </div>
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
                <p className="text-xs text-muted-foreground">
                  Override the subscription expiry date. Leave empty to use the default.
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="pta" className="space-y-4 mt-4">
              {subscription?.pta_id ? (
                <>
                  <div className="space-y-2">
                    <Label>PTA Name</Label>
                    <Input value={ptaName} onChange={(e) => setPtaName(e.target.value)} placeholder="e.g., Hillside Primary PTA" />
                  </div>
                  <div className="space-y-2">
                    <Label>School Name</Label>
                    <Input value={schoolName} onChange={(e) => setSchoolName(e.target.value)} placeholder="e.g., Hillside Primary School" />
                  </div>
                  <div className="space-y-2">
                    <Label>Charity Number (Optional)</Label>
                    <Input value={charityNumber} onChange={(e) => setCharityNumber(e.target.value)} placeholder="e.g., 123456" />
                  </div>
                  <div className="space-y-2">
                    <Label>Shared Email (Optional)</Label>
                    <Input type="email" value={sharedEmail} onChange={(e) => setSharedEmail(e.target.value)} placeholder="e.g., pta@school.com" />
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No PTA associated with this subscription. PTA details can only be edited for subscriptions with linked PTAs.
                </p>
              )}
            </TabsContent>

            <TabsContent value="addons" className="space-y-4 mt-4">
              {plan === 'standard' ? (
                <p className="text-sm text-muted-foreground">
                  Standard plan includes all features. Add-ons are only applicable for Basic plan subscriptions.
                </p>
              ) : allAddons.length === 0 ? (
                <p className="text-sm text-muted-foreground">No add-ons available.</p>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">Toggle add-ons for this Basic plan subscription.</p>
                  {allAddons.map((addon) => (
                    <div key={addon.id} className="flex items-start gap-3 p-3 border border-border rounded-lg">
                      <Checkbox
                        id={`addon-${addon.id}`}
                        checked={activeAddonIds.includes(addon.id)}
                        onCheckedChange={() => toggleAddon(addon.id)}
                      />
                      <div className="flex-1">
                        <label htmlFor={`addon-${addon.id}`} className="text-sm font-medium cursor-pointer">
                          {addon.name}
                        </label>
                        {addon.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{addon.description}</p>
                        )}
                      </div>
                      <span className="text-sm font-medium text-primary">£{addon.price?.toFixed(2)}/yr</span>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
